import type { LoremDocSettings } from "./settings";
import type { LoremSourceName } from "./types";

// Slash-command grammar for lorem triggers (Engineering brief §C4 + §C5).
//
//   /lorem           → use doc default count + source
//   /lorem 5         → override count to 5, source from doc default
//   /lorem-tech      → override source to lorem-tech, count from doc default
//   /lorem-tech 7    → both overrides
//   /lorem-math 25   → math source, max-allowed count
//   /lorem 100       → clamp to 25, signal countOverflow=true so the slash
//                       menu can show the "Capped at 25" toast (Q1.4 locked
//                       decision)
//
// The slash menu owns the actual insertion + toast emission — this module
// only parses and validates.

export const LOREM_COUNT_MAX = 25;

export interface ParsedLoremSlash {
	/** Override source, or null when the user typed bare `/lorem`. */
	source: LoremSourceName | null;
	/** Override count, or null when no count was provided. */
	count: number | null;
	/** True when the user typed a count > LOREM_COUNT_MAX (we clamped). */
	countOverflow: boolean;
	/** The raw command (everything before the count). */
	command: "lorem" | "lorem-tech" | "lorem-math";
}

export interface ResolvedLoremSlash {
	source: LoremSourceName;
	count: number;
	/** True when count had to be clamped to LOREM_COUNT_MAX. */
	clamped: boolean;
}

const COMMAND_PATTERN = /^\/(lorem(?:-tech|-math)?)(?:\s+(\d+))?\s*$/i;

/**
 * Parse a slash-command string into structured fields. Returns null
 * for any input that isn't a recognised lorem command.
 *
 * Whitespace tolerance:
 *   - Leading/trailing whitespace ignored
 *   - Multiple spaces between command and count allowed
 *   - Case-insensitive command name (/Lorem 5 == /lorem 5)
 *
 * Non-integer counts (`/lorem 3.5`, `/lorem ten`) → returns null.
 * Zero and negative counts (`/lorem 0`, `/lorem -3`) → returns null.
 *
 * Overflow (`/lorem 100`) is clamped to LOREM_COUNT_MAX with the
 * `countOverflow` flag set; the slash menu surfaces a toast in
 * response per Q1.4.
 */
export function parseLoremSlash(input: string): ParsedLoremSlash | null {
	const trimmed = input.trim();
	const match = trimmed.match(COMMAND_PATTERN);
	if (!match) return null;

	const rawCmd = match[1].toLowerCase() as ParsedLoremSlash["command"];
	const rawCount = match[2];

	// Map command → source override. Bare `/lorem` returns null source so
	// the caller falls back to the doc default (which could already be
	// lorem-tech or lorem-math via the settings panel).
	const source: LoremSourceName | null =
		rawCmd === "lorem-tech"
			? "lorem-tech"
			: rawCmd === "lorem-math"
				? "lorem-math"
				: null;

	let count: number | null = null;
	let countOverflow = false;
	if (rawCount !== undefined) {
		const parsed = Number.parseInt(rawCount, 10);
		if (!Number.isFinite(parsed) || parsed <= 0) return null;
		if (parsed > LOREM_COUNT_MAX) {
			count = LOREM_COUNT_MAX;
			countOverflow = true;
		} else {
			count = parsed;
		}
	}

	return { source, count, countOverflow, command: rawCmd };
}

/**
 * Resolve a parsed slash against the doc settings → concrete
 * { source, count } payload for the lorem generator.
 *
 * The slash menu calls this right before invoking `generate(...)`.
 */
export function resolveLoremSlash(
	parsed: ParsedLoremSlash,
	docSettings: LoremDocSettings,
): ResolvedLoremSlash {
	return {
		source: parsed.source ?? docSettings.source,
		count: parsed.count ?? docSettings.count,
		clamped: parsed.countOverflow,
	};
}

/**
 * Convenience matcher — returns true iff `input` could be the start
 * of a lorem command. Used by the slash menu to highlight the lorem
 * option without committing to a full parse.
 */
export function isLoremSlashPrefix(input: string): boolean {
	return /^\/lorem(-tech|-math)?(\s|$)/i.test(input.trim());
}
